import React, { useEffect, useState } from "react";
import { feedApi } from "../services/api";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { getOrCreateDeviceId } from "../services/device";

/* ================== CONSTANTS ================== */
const PAGE_SIZE = 20;
const CATEGORY_SOFT_THRESHOLD = 2;

/* ================== SYSTEM OWNED IMAGES ================== */
const SUBCATEGORY_IMAGES = {
  shawarma: ["shawarma-1.png", "shawarma-2.png"],
  cakes: ["cakes-1.png", "cafe-2.png"],
  cafe: ["cafe-1.png", "burger-1.png", "pizza-1.png"],
  "fast-food": ["fast-food-1.png", "burger-2.png"],
  burkha: ["abaya-1.png", "abaya-2.png"],
  ladies: ["ladies.png"],
};

/* ================== BOOST EXTRACTION ================== */
function extractBoostDeal(campaigns) {
  const idx = campaigns.findIndex(
    (c) => c.category === "food" && c.campaignType === "COUPON"
  );

  if (idx === -1) return { boost: null, rest: campaigns };

  return {
    boost: campaigns[idx],
    rest: [...campaigns.slice(0, idx), ...campaigns.slice(idx + 1)],
  };
}

/* ================== IMAGE RESOLVER ================== */
function getFeedImage({ campaign, index }) {
  const pool = SUBCATEGORY_IMAGES[campaign.subcategory];
  if (!pool) return null;
  return `/assets/${pool[index % pool.length]}`;
}

/* ================== ANALYTICS ================== */
export const trackCampaignViewed = ({ campaignId, merchantId }) => {
  if (process.env.REACT_APP_ENABLE_GA !== "true") return;
  ReactGA.event("campaign_viewed", {
    campaign_id: campaignId,
    merchant_id: merchantId,
  });
};

/* ================== INFO STRIP ================== */
function FeedInfoCard({ title, subtitle }) {
  return (
    <div className="w-full bg-card-light dark:bg-card-dark border-b border-border-light dark:border-border-dark">
      <div className="mx-auto w-full max-w-[450px] px-6 py-3">
        <p className="text-xs font-medium text-text-main-light dark:text-text-main-dark">
          {title}
        </p>
        {subtitle && (
          <p className="mt-0.5 text-xs text-text-secondary-light dark:text-text-secondary-dark">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

/* ================== SKELETON ================== */
function SkeletonCard() {
  return (
    <div className="mx-6 mb-6 h-40 rounded-2xl bg-border-light dark:bg-border-dark animate-pulse" />
  );
}

/* ================== TIME BUCKET ================== */
const getTimeBucket = () => {
  const h = new Date().getHours();
  if (h >= 20) return "NIGHT";
  if (h >= 17) return "EVENING";
  return "DAY";
};

/* ================== INTENT ================== */
const normalizeIntent = (c) => {
  if (c.category === "food") {
    if (["cakes", "desserts"].includes(c.subcategory)) return "DESSERTS";
    if (["shawarma", "fast-food", "cafe"].includes(c.subcategory))
      return "QUICK_BITES";
    return "FOOD";
  }
  if (c.category === "modest-wear") return "MODEST_WEAR";
  return "OTHER";
};

/* ================== FEED COMPOSER ================== */
const composeFeedSections = (campaigns) => {
  const time = getTimeBucket();
  const { boost, rest } = extractBoostDeal(campaigns);

  const food = [];
  const modestWear = [];
  const misc = [];

  rest.forEach((c) => {
    const intent = normalizeIntent(c);
    if (["FOOD", "DESSERTS", "QUICK_BITES"].includes(intent)) food.push(c);
    else if (intent === "MODEST_WEAR") modestWear.push(c);
    else misc.push(c);
  });

  const sections = [];

  if (boost) sections.push({ type: "boost", items: [boost] });

  if (time === "NIGHT") {
    const desserts = food.filter((c) => normalizeIntent(c) === "DESSERTS");
    if (desserts.length >= CATEGORY_SOFT_THRESHOLD) {
      sections.push({
        type: "horizontal",
        title: "Custom deals for tonight",
        items: desserts,
      });
      desserts.forEach((d) => food.splice(food.indexOf(d), 1));
    }
  }

  if (time === "EVENING") {
    const quick = food.filter((c) => normalizeIntent(c) === "QUICK_BITES");
    if (quick.length >= CATEGORY_SOFT_THRESHOLD) {
      sections.push({
        type: "horizontal",
        title: "Custom deals near you",
        items: quick,
      });
      quick.forEach((q) => food.splice(food.indexOf(q), 1));
    }
  }

  if (food.length)
    sections.push({
      type: "mixed",
      title: "Nearby food deals",
      items: food,
    });

  if (modestWear.length)
    sections.push({
      type: "horizontal",
      title: "Abaya deals",
      items: modestWear,
    });

  if (misc.length)
    sections.push({
      type: "mixed",
      title: "Everything else",
      items: misc,
    });

  return sections;
};

/* ================== FEED CARD ================== */
function FeedVisualCard({ campaign, index, onClick, variant = "mixed" }) {
  const image = getFeedImage({ campaign, index });

  return (
    <div
      onClick={onClick}
      className="
    rounded-2xl
    bg-card-light dark:bg-card-dark
    border border-border-light dark:border-border-dark
    shadow-soft dark:shadow-dark
    cursor-pointer
    overflow-hidden
    active:scale-[0.97]
  "
    >
      {image && (
        <div className="relative w-full aspect-[16/9] bg-border-light dark:bg-border-dark">
          <img src={image} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      <div className="px-4 py-3">
        <p
          className="text-lg font-bold text-text-main-light dark:text-text-main-dark line-clamp-1"
          style={{ minHeight: "1.4rem" }}
        >
          {campaign.merchantName}
        </p>

        <p
          className={`
    text-xs
    text-text-secondary-light dark:text-text-secondary-dark
    line-clamp-2
    leading-[1.25rem]
    ${variant === "horizontal" ? "min-h-[2.5rem]" : ""}
  `}
        >
          <span className="mr-1">✪</span>
          {campaign.title}
        </p>
      </div>
    </div>
  );
}

/* ================== EXCLUSIVE CARD ================== */
function ExclusiveDealCard({ campaign, onClick }) {
  const image = getFeedImage({ campaign, index: 0 });

  return (
    <div className="px-6 pt-6 pb-2">
      <div className="mb-2 flex items-center gap-2">
        <span className="h-px flex-1 bg-primary/20" />
        <span className="text-[11px] font-semibold tracking-widest text-primary">
          EXCLUSIVE DEAL
        </span>
        <span className="h-px flex-1 bg-primary/20" />
      </div>

      <div
        onClick={onClick}
        className="
          rounded-2xl
          bg-gradient-to-br from-card-light to-[#FFF6EE]
          dark:from-card-dark dark:to-[#1a1a1a]
          border border-primary/20
shadow-[0_12px_28px_rgba(0,0,0,0.18)]
dark:shadow-[0_0_0_1px_rgba(255,107,0,0.25),0_16px_40px_rgba(0,0,0,0.75)]
          cursor-pointer overflow-hidden active:scale-[0.98]
        "
      >
        {image && (
          <div className="relative aspect-[16/9]">
            <img src={image} alt="" className="h-full w-full object-cover" />
          </div>
        )}

        <div className="px-5 py-4">
          <p className="text-[20px] font-extrabold text-text-main-light dark:text-text-main-dark">
            {campaign.merchantName}
          </p>

          <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark line-clamp-2">
            {campaign.title}
          </p>

          <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
            View deal →
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================== SECTIONS ================== */
function SectionHeader({ title }) {
  return (
    <div className="px-6 pt-8 pb-2">
      <h2 className="text-lg font-semibold text-text-main-light dark:text-text-main-dark">
        {title}
      </h2>
    </div>
  );
}

function HorizontalSection({ items, onClick }) {
  return (
    <div className="flex gap-4 overflow-x-auto no-scrollbar px-6 pb-6">
      {items.map((c, i) => (
        <div key={c.campaignId} className="min-w-[220px]">
          <FeedVisualCard campaign={c} index={i} onClick={() => onClick(c)} variant="horizontal" />
        </div>
      ))}
    </div>
  );
}

function MixedSection({ items, onClick }) {
  return (
    <div className="grid grid-cols-1 gap-6 px-6 pb-6">
      {items.map((c, i) => (
        <FeedVisualCard
          key={c.campaignId}
          campaign={c}
          index={i}
          onClick={() => onClick(c)}
          variant="mixed"
        />
      ))}
    </div>
  );
}

/* ================== MAIN ================== */
export default function Feed() {
  const [feedCampaigns, setFeedCampaigns] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    ReactGA.set({ device_id: getOrCreateDeviceId() });
    (async () => {
      setFeedLoading(true);
      const res = await feedApi.getFeed({ page: 0, size: PAGE_SIZE });
      setFeedCampaigns(res?.items || []);
      setFeedLoading(false);
    })();
  }, []);

  const sections = composeFeedSections(feedCampaigns);

  return (
    <div className="mx-auto w-full max-w-[450px] pb-12 bg-background-light dark:bg-background-dark overflow-x-hidden">
      <FeedInfoCard title="No payment • Show at shop" />

      {feedLoading ? (
        <>
          <SkeletonCard />
          <SkeletonCard />
        </>
      ) : (
        sections.map((section, idx) =>
          section.type === "boost" ? (
            <ExclusiveDealCard
              key={`boost-${idx}`}
              campaign={section.items[0]}
              onClick={() =>
                navigate(`/merchant/${section.items[0].merchantNameId}`)
              }
            />
          ) : (
            <React.Fragment key={`${section.type}-${idx}`}>
              <SectionHeader title={section.title} />
              {section.type === "horizontal" ? (
                <HorizontalSection
                  items={section.items}
                  onClick={(c) => navigate(`/merchant/${c.merchantNameId}`)}
                />
              ) : (
                <MixedSection
                  items={section.items}
                  onClick={(c) => navigate(`/merchant/${c.merchantNameId}`)}
                />
              )}
            </React.Fragment>
          )
        )
      )}

      <FeedInfoCard
        title="⏰ New deals are coming soon"
        subtitle="Deals are updated daily"
      />
    </div>
  );
}
